'use client';

import { Card, Label } from '@repo/ui';
import { ScrollableContainer } from '../common/scrollable-container';
import { DistributionGraph } from '../common/distribution-graph';
import { ModeResult } from '../../../lib/stats/mode';

interface ModeCalculatorProps {
  visualizationData: ModeResult | null;
}

export function ModeCalculator({ visualizationData: result }: ModeCalculatorProps) {
  if (!result) {
    return null;
  }

  // Get the highest frequency
  const maxFrequency = Math.max(...Object.values(result.frequencies));
  
  // Show only frequencies that are at least 50% of the max frequency
  const significantFrequencies = Object.entries(result.frequencies)
    .filter(([_, freq]) => freq >= maxFrequency * 0.5)
    .sort(([_, a], [__, b]) => b - a)
    .slice(0, 5); // Limit to top 5 most frequent values

  return (
    <ScrollableContainer>
      <div className="space-y-6">
        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Mode Results</h3>
          <div className="space-y-4">
            <div>
              <Label>Mode Values</Label>
              <div className="text-lg font-semibold">
                {result.modes.length > 0 
                  ? result.modes.map(n => n.toFixed(2)).join(', ')
                  : 'No mode found'}
              </div>
            </div>
            <div>
              <Label>Most Common Values</Label>
              <div className="text-sm grid grid-cols-2 gap-2">
                {significantFrequencies.map(([value, freq]) => (
                  <div key={value} className="flex justify-between">
                    <span>{Number(value).toFixed(2)}:</span>
                    <span>{freq} times</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label>Interpretation</Label>
              <div className="text-sm text-muted-foreground">{result.interpretation}</div>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Distribution Plot</h3>
          <DistributionGraph
            data={result.data}
            height={300}
            showMode
            grid={{
              left: '15%',
              right: '5%',
              top: '10%',
              bottom: '15%'
            }}
          />
        </Card>
      </div>
    </ScrollableContainer>
  );
} 